import { useEffect, useRef, useState } from 'react';
import { usePreferenze } from '../api/sessione.js';
import { type Lingua, cambiaLingua, lingua, t } from '../i18n/lingua.js';
import { type Aspetto, applicaAspetto } from '../tema.js';

const VOCI: { valore: Aspetto; segno: string; etichetta: string }[] = [
  { valore: 'light', segno: '☀', etichetta: t('Chiaro') },
  { valore: 'dark', segno: '☾', etichetta: t('Scuro') },
  { valore: 'system', segno: '◐', etichetta: t('Come il sistema') },
];

const LINGUE: { valore: Lingua; segno: string; etichetta: string }[] = [
  { valore: 'it', segno: 'IT', etichetta: t('Italiano') },
  { valore: 'en', segno: 'EN', etichetta: t('Inglese') },
];

/**
 * Aspetto e lingua.
 *
 * Stanno insieme perché sono la stessa cosa — come si vede il programma, non
 * cosa contiene — e perché due menu accanto per due preferenze che si toccano
 * una volta l'anno sarebbero due bottoni sprecati nella barra.
 *
 * Il cambiamento si vede subito e viene salvato sul profilo: se la scrittura
 * fallisce, la scelta resta comunque valida per questa sessione — è una
 * preferenza, non un dato da difendere.
 */
export function MenuAspetto({ aspetto }: { aspetto: Aspetto }) {
  const [aperto, setAperto] = useState(false);
  const contenitore = useRef<HTMLDivElement>(null);
  const preferenze = usePreferenze();
  const inUso = lingua();

  useEffect(() => {
    if (!aperto) return;
    const chiudi = (e: MouseEvent) => {
      if (!contenitore.current?.contains(e.target as Node)) setAperto(false);
    };
    const conEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAperto(false);
    };
    document.addEventListener('mousedown', chiudi);
    document.addEventListener('keydown', conEsc);
    return () => {
      document.removeEventListener('mousedown', chiudi);
      document.removeEventListener('keydown', conEsc);
    };
  }, [aperto]);

  const scegli = (valore: Aspetto) => {
    applicaAspetto(valore);
    setAperto(false);
    preferenze.mutate({ theme: valore });
  };

  /** La lingua si salva sul profilo e poi la pagina si ricarica. */
  const scegliLingua = (valore: Lingua) => {
    if (valore === inUso) return setAperto(false);
    preferenze.mutate({ language: valore });
    cambiaLingua(valore);
  };

  const scuro = document.documentElement.dataset.theme === 'dark';

  return (
    <div className="menu-wrap" ref={contenitore}>
      <button
        className="icon-btn"
        title={t('Aspetto')}
        aria-haspopup="menu"
        aria-expanded={aperto}
        onClick={() => setAperto((v) => !v)}
      >
        {scuro ? '☾' : '☀'}
      </button>
      {aperto && (
        <div className="menu" role="menu">
          <div className="menu-label">{t('Aspetto')}</div>
          {VOCI.map((voce) => (
            <button
              key={voce.valore}
              className="menu-item"
              role="menuitemradio"
              aria-checked={aspetto === voce.valore}
              onClick={() => scegli(voce.valore)}
            >
              <span className="glyph">{voce.segno}</span> {t(voce.etichetta)}
              <span className="tick">✓</span>
            </button>
          ))}

          <div className="menu-label">{t('Lingua')}</div>
          {LINGUE.map((voce) => (
            <button
              key={voce.valore}
              className="menu-item"
              role="menuitemradio"
              aria-checked={inUso === voce.valore}
              onClick={() => scegliLingua(voce.valore)}
            >
              <span className="glyph">{voce.segno}</span> {t(voce.etichetta)}
              <span className="tick">✓</span>
            </button>
          ))}

          <div className="menu-note">
            {t('La scelta resta sul tuo profilo. Cambiare lingua ricarica la pagina.')}
          </div>
        </div>
      )}
    </div>
  );
}
